/**
 * Hook for fetching invoice view counts.
 *
 * Fetches counts for each view tab (All, To Pay, To Review, Paid, Overdue).
 */

import { useQuery } from '@tanstack/react-query';

import { invoicesApi } from '@/api/invoices';
import { queryKeys } from '@/lib/query-client';
import type { InvoiceListFilters, InvoiceViewCounts } from '@/types/invoices';

/**
 * Return type for useInvoicesCounts hook.
 */
export interface UseInvoicesCountsReturn {
  /** Counts for each view */
  counts: InvoiceViewCounts | undefined;
  /** Whether the data is loading */
  isLoading: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if an error occurred */
  error: Error | null;
  /** Refetch the counts */
  refetch: () => void;
}

/**
 * Hook options.
 */
export interface UseInvoicesCountsOptions {
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
  /** Scope filters for counts (period/supplier/location/etc.) */
  filters?: InvoiceListFilters;
}

/**
 * Hook for fetching invoice view counts.
 *
 * @param options - Hook options
 * @returns Counts data and query state
 *
 * @example
 * ```tsx
 * function InvoicesPage() {
 *   const { counts, isLoading } = useInvoicesCounts();
 *
 *   // Pass counts to InvoiceViewsTabs for display
 * }
 * ```
 */
export function useInvoicesCounts(options: UseInvoicesCountsOptions = {}): UseInvoicesCountsReturn {
  const { enabled = true, filters = {} } = options;

  const query = useQuery({
    queryKey: queryKeys.invoices.counts(filters),
    queryFn: () => invoicesApi.getCounts(filters),
    enabled,
    // Refetch counts less frequently - they update when invoices change
    staleTime: 30 * 1000, // 30 seconds
    // Keep counters fresh when returning from detail/other pages.
    refetchOnMount: 'always',
  });

  return {
    counts: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
